import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { settingsAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const STORAGE_KEY = 'metal_rates_v1';

// Default purities commonly used
const DEFAULT_RATES = {
  gold: {
    'Standard': 0,
    '18K': 0,
    '20K': 0,
    '22K': 0,
    '24K': 0,
  },
  silver: {
    'Standard': 0,
    'Fine': 0,
    'Sterling(92.5%)': 0,
  },
};

const RatesContext = createContext(null);

export const RatesProvider = ({ children }) => {
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [loadedFromServer, setLoadedFromServer] = useState(false);
  const isDirtyRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const { user } = useAuth();
  const canEdit = user && (user.role === 'admin' || user.role === 'manager');

  // Initial load: try server first, fallback to localStorage
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await settingsAPI.getMetalRates();
        if (!cancelled && res?.data?.data) {
          setRates((prev) => ({ ...prev, ...res.data.data }));
          setLoadedFromServer(true);
          // Also sync to localStorage
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data.data)); } catch (_) {}
          return;
        }
      } catch (_) {
        // ignore, fallback to localStorage
      }

      // Fallback to localStorage
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw && !cancelled) {
          const parsed = JSON.parse(raw);
          setRates((prev) => ({ ...prev, ...parsed }));
        }
      } catch (_) {}
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Persist to localStorage on any change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rates)); } catch (_) {}
  }, [rates]);

  const updateRate = useCallback((metal, purity, valuePerGram) => {
    // Block edits for roles without permission
    if (!canEdit) return;
    setRates((prev) => {
      const next = {
        ...prev,
        [metal]: {
          ...(prev[metal] || {}),
          [purity]: Number(valuePerGram) || 0,
        },
      };
      isDirtyRef.current = true;
      return next;
    });
  }, [canEdit]);

  // Debounced save to server only when user changes values
  useEffect(() => {
    if (!loadedFromServer && !isDirtyRef.current) return; // wait until initial load or user change
    if (!isDirtyRef.current) return;
    if (!canEdit) return; // do not attempt saving if user cannot edit

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payload = rates;
        await settingsAPI.updateMetalRates(payload);
        isDirtyRef.current = false;
      } catch (_) {
        // silent fail; will try again on next change
      }
    }, 600);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [rates, loadedFromServer, canEdit]);

  const getRate = useCallback((metal, purity) => {
    return Number(rates?.[metal]?.[purity] || 0);
  }, [rates]);

  const replaceRates = useCallback((newRates, markDirty = false) => {
    setRates((prev) => ({ ...prev, ...newRates }));
    if (markDirty) {
      isDirtyRef.current = true;
    } else {
      isDirtyRef.current = false;
    }
  }, []);

  const contextValue = useMemo(() => ({ rates, updateRate, getRate, canEdit, replaceRates }), [rates, updateRate, getRate, canEdit, replaceRates]);

  return (
    <RatesContext.Provider value={contextValue}>
      {children}
    </RatesContext.Provider>
  );
};

export const useRates = () => {
  const ctx = useContext(RatesContext);
  if (!ctx) throw new Error('useRates must be used within RatesProvider');
  return ctx;
};


